import { useLang } from "../../contexts/LangContext";
import { useNavigate } from "react-router-dom";
import { useLogSession } from "../../contexts/LogSessionContext";
import { useUserData } from "../../contexts/UserContext";
import FishLog from "../../cards/FishLog";
import { useEffect, useRef, useState } from "react";
import { AppSettings, DayRecord, MealRecord, PersonRecord } from "../../types/data";
import fishSwimmingGif from "../../assets/site-images/fish-swimming.gif";
import { logService } from "../../services/log.service";
import { storageService, shouldShowExtraInfo } from "../../services/storage.service";
import { fishDatabase } from "../../data/fish_db";

export default function ReviewFishItem() {
  const {    
    currentDayRecord,
    currentMealRecord,
    setcurrentMealRecord,
    newFishItem,
    setNewFishItem,
    commitMealToDay,
    trackFishItemInSession,
    exitFallback,
    person,
  } = useLogSession();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const { updatePerson } = useUserData();
  const isNavigatingAway = useRef(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showEditFlyout, setShowEditFlyout] = useState(false);

  const checkLogSession = () => {
    // Don't redirect if we're intentionally navigating away
    if (isNavigatingAway.current) return;
    
    if (!currentDayRecord) {
      navigate("../any-fish-today", { replace: true });
      return;
    } else if (!currentMealRecord) {
      navigate("../which-meal", { replace: true });
      return;
    } else if (!newFishItem) {
      navigate("../which-fish", { replace: true });
      return;
    }
  };

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
  }, []);

  useEffect(() => {
    checkLogSession();
  }, [currentDayRecord, currentMealRecord, newFishItem, navigate]);

  const [settings, setSettings] = useState<AppSettings | null>(null);

  useEffect(() => {
    storageService.getSettings().then(setSettings).catch(() => {});
  }, []);

  // Show loading overlay first, before any error checks
  if (isSaving) {
    return (
      <div className="fixed inset-0 bg-white bg-opacity-50 backdrop-blur flex items-center justify-center z-50">
        <div className="p-8 flex flex-col items-center gap-4">
          <img src={fishSwimmingGif} alt="Loading..." className="w-40 h-auto" />
          <p className="text-xl font-semibold text-gray-700">{t("reviewLog_Saving")}</p>
        </div>
      </div>
    );
  }

  if(!newFishItem) {
    return (
      <div className="p-8 text-center text-red-600">
        {t("ReviewFishItem_noFishItem")}
      </div>
    );
  }

  if(!currentMealRecord) {
    return (
      <div className="p-8 text-center text-red-600">
        {t("ReviewFishItem_noMealRecord")}
      </div>
    );
  }

  const displayFishName = newFishItem.fish_name || t("unnamed") || "Unnamed";
  const hasSoftBone =
    fishDatabase.find((f) => f.id === newFishItem.fish_id)?.has_soft_bone || false;

  const handleConfirm = () => {
    // Always add the fish item to the meal, updating if it already exists
    const existingItemIndex = currentMealRecord.fish_items.findIndex(
      (item) => item.id === newFishItem.id
    );
    if (existingItemIndex !== -1) {
      const updatedItems = [...currentMealRecord?.fish_items];
      updatedItems[existingItemIndex] = newFishItem;
      setcurrentMealRecord({ 
        ...currentMealRecord, 
        fish_items: updatedItems,
        updated_at: new Date().toISOString(),
      });
    } else {
      setcurrentMealRecord({
        ...currentMealRecord,
        fish_items: [...currentMealRecord.fish_items, newFishItem],
        updated_at: new Date().toISOString(),
      });
    }
    navigate("../add-another-fish-item");
  };

  const handleEdit = () => {
    setShowEditFlyout(true);
  };

  const handleEditOption = (destination: string) => {
    setShowEditFlyout(false);
    navigate(destination);
  };

  const verifySaved = async (updatedMealRecord: MealRecord, retries: number = 3): Promise<boolean> => {
    // Safety checks
    if (!person || !currentDayRecord || !newFishItem) {
      logService.warn('Review Fish Item', 'verifySaved: Missing required data', {
        hasPerson: !!person,
        hasDayRecord: !!currentDayRecord,
        hasFishItem: !!newFishItem
      });
      return false;
    }

    try {
      // Read fresh data from storage to verify the save
      const userData = await storageService.getUserData();
      
      if (!userData || !userData.persons) {
        logService.warn('Review Fish Item', 'verifySaved: No user data found in storage');
        return false;
      }
      
      // Find the person record in storage
      const personRecord = userData.persons.find((p: PersonRecord) => p.id === person.id);
      
      if (!personRecord) {
        logService.warn('Review Fish Item', 'verifySaved: Person not found in storage', {
          personId: person.id
        });
        return false;
      }
      
      // Find the day record
      const dayRecord = personRecord.records?.find((day: DayRecord) => day.date === currentDayRecord.date);
      if (!dayRecord) {
        logService.warn('Review Fish Item', 'verifySaved: Day record not found', {
          date: currentDayRecord.date
        });
        
        if (retries > 0) {
          logService.info('Review Fish Item', `verifySaved: Retrying (${retries} attempts left)...`);
          await new Promise(resolve => setTimeout(resolve, 2000));
          return verifySaved(updatedMealRecord, retries - 1);
        }
        return false;
      }
      
      // Find the meal record with the new fish item
      const mealRecord = dayRecord.meals?.find((meal: MealRecord) => 
        meal.meal_type === updatedMealRecord.meal_type && 
        meal.fish_items?.some((item) => item.id === newFishItem.id)
      );
      
      if (mealRecord) {
        logService.info('Review Fish Item', 'verifySaved: Meal successfully verified in storage');
        return true;
      }
      
      logService.warn('Review Fish Item', 'verifySaved: Meal not found or fish item missing', {
        mealType: updatedMealRecord.meal_type,
        fishItemId: newFishItem.id
      });
      
      if (retries > 0) {
        logService.info('Review Fish Item', `verifySaved: Retrying (${retries} attempts left)...`);
        await new Promise(resolve => setTimeout(resolve, 2000));
        return verifySaved(updatedMealRecord, retries - 1);
      }
      
      return false;
    } catch (error) {
      logService.error('Review Fish Item', 'verifySaved: Error verifying saved data', {
        error: error instanceof Error ? error.message : String(error)
      });
      
      if (retries > 0) {
        logService.info('Review Fish Item', `verifySaved: Retrying after error (${retries} attempts left)...`);
        await new Promise(resolve => setTimeout(resolve, 2000));
        return verifySaved(updatedMealRecord, retries - 1);
      }
      
      return false;
    }
  };

  const handleConfirmAndFinish = async () => {
    // Set flag to prevent useEffect from redirecting
    isNavigatingAway.current = true;
    
    // Safety check - these should exist, but TypeScript requires the check
    if (!currentDayRecord || !currentMealRecord || !newFishItem) {
      logService.error('Review Fish Item', 'Missing required data for handleConfirmAndFinish', {
        hasDayRecord: !!currentDayRecord,
        hasMealRecord: !!currentMealRecord,
        hasFishItem: !!newFishItem
      });
      return;
    }
    
    setIsSaving(true);
        
    try {
      // console.log('[ReviewFishItem] Starting save operation...');
      
      // First, update the current meal record with the fish item
      const existingItemIndex = currentMealRecord.fish_items.findIndex(
        (item) => item.id === newFishItem.id
      );
      
      // Track this fish item in the current session
      trackFishItemInSession(newFishItem.id);
      
      let updatedMealRecord: MealRecord;
      if (existingItemIndex !== -1) {
        // If it exists, update the existing item
        const updatedItems = [...currentMealRecord.fish_items];
        updatedItems[existingItemIndex] = newFishItem;
        updatedMealRecord = { 
          ...currentMealRecord, 
          fish_items: updatedItems,
          updated_at: new Date().toISOString(),
        };
      } else {
        // If it doesn't exist, add the new fish item to the meal
        updatedMealRecord = {
          ...currentMealRecord,
          fish_items: [...currentMealRecord.fish_items, newFishItem],
          updated_at: new Date().toISOString(),
        };
      }
      
      // Update the session state with the new meal
      setcurrentMealRecord(updatedMealRecord);
      
      // Retry loop: Commit -> Wait -> Verify, up to 3 attempts
      let commitSuccess = false;
      const maxCommitAttempts = 3;
      
      for (let attempt = 1; attempt <= maxCommitAttempts; attempt++) {
        logService.info('Review Fish Item', `Commit attempt ${attempt}/${maxCommitAttempts}`);
        
        try {
          // Commit meal to storage
          await commitMealToDay(true, updatedMealRecord);
          logService.info('Review Fish Item', 'commitMealToDay completed');
          
          // Wait 2 seconds for save animation and storage flush
          await new Promise(resolve => setTimeout(resolve, 2000));
          
          // Verify the save
          logService.info('Review Fish Item', 'Verifying save operation...');
          const saveVerified = await verifySaved(updatedMealRecord);
          
          if (saveVerified) {
            logService.info('Review Fish Item', `Save verified successfully on attempt ${attempt}`);
            commitSuccess = true;
            break; // Success! Exit retry loop
          } else {
            logService.warn('Review Fish Item', `Verification failed on attempt ${attempt}`);
            
            if (attempt < maxCommitAttempts) {
              logService.info('Review Fish Item', `Retrying commit (${maxCommitAttempts - attempt} attempts remaining)...`);
              // Add extra delay before retry
              await new Promise(resolve => setTimeout(resolve, 1000));
            }
          }
        } catch (error) {
          logService.error('Review Fish Item', `Commit attempt ${attempt} threw error`, {
            error: error instanceof Error ? error.message : String(error),
            stack: error instanceof Error ? error.stack : undefined
          });
          
          if (attempt < maxCommitAttempts) {
            logService.info('Review Fish Item', `Retrying after error (${maxCommitAttempts - attempt} attempts remaining)...`);
            await new Promise(resolve => setTimeout(resolve, 1000));
          } else {
            // Last attempt failed, throw error
            throw error;
          }
        }
      }
      
      if (!commitSuccess) {
        const errorMsg = t('reviewFishItem_SaveVerificationFailed');
        logService.error('Review Fish Item', errorMsg, {
          personId: person?.id,
          date: currentDayRecord?.date,
          mealType: updatedMealRecord.meal_type,
          fishItemId: newFishItem.id,
          attempts: maxCommitAttempts
        });
        alert(errorMsg);
        setIsSaving(false);
        isNavigatingAway.current = false;
        return;
      }

      logService.info('Review Fish Item', 'Save operation completed and verified');
      
      // Navigate first (don't clear state until after to prevent error message)
      navigate("../submitted", { replace: true });
      
      // Clear session state after navigation starts
      setTimeout(() => {
        setNewFishItem(null);
        setcurrentMealRecord(null);
      }, 100);
    } catch (error) {
      logService.error('Review Fish Item', 'Failed to save meal', {
        error: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined
      });
      alert(`Failed to save: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setIsSaving(false);
      isNavigatingAway.current = false;
    }
  };

  return (
    <div className="w-full grow flex flex-col items-center justify-start gap-6 text-center px-scr pt-8 relative">
      <h1 className="text-2xl font-bold text-rose-700">
        {t("reviewFishItem_Heading")}
      </h1>

      <div className="rounded-3xl w-full p-4 text-left text-lg bg-gray-100/50 backdrop-blur">
        <FishLog item={newFishItem} />
      </div>

      <div className="flex flex-col gap-4 w-full max-w-sm mb-8">
          <button
            onClick={handleConfirm}
            className="flex items-center justify-center gap-3 button-small button-bg-grad-green disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isSaving}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
              className="size-6"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m4.5 12.75 6 6 9-13.5"
              />
            </svg>
            <span>{t("reviewFishItem_Confirm")}</span>
          </button>
          <button
            onClick={handleConfirmAndFinish}
            className="flex items-center justify-center gap-3 button-small button-bg-grad-1 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isSaving}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
              className="size-6"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
              />
            </svg>
            <span>{t("reviewFishItem_ConfirmAndFinish")}</span>
          </button>
          <button
            onClick={handleEdit}
            className="flex items-center justify-center gap-3 button-small button-bg-grad-2 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isSaving}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
              className="size-6"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10"
              />
            </svg>
            <span>{t("reviewFishItem_Edit")}</span>
          </button>
        </div>

      {/* Edit Flyout */}
      {showEditFlyout && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-30 backdrop-blur-sm z-40 transition-opacity"
            onClick={() => setShowEditFlyout(false)}
          />
          
          {/* Flyout */}
          <div className="fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl shadow-2xl z-50 animate-slide-up">
            <div className="p-6 pb-8">
              {/* Handle bar */}
              <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-4" />
              
              {/* Title */}
              <h2 className="text-xl font-bold text-gray-800 mb-6 text-center">
                {t("reviewFishItem_EditWhat")}
              </h2>
              
              {/* Options */}
              <div className="flex flex-col gap-3">
                <button
                  onClick={() => handleEditOption("../which-fish")}
                  className="flex items-center gap-4 p-4 bg-gradient-to-r from-blue-50 to-blue-100 hover:from-blue-100 hover:to-blue-200 rounded-xl transition-all shadow-sm hover:shadow-md"
                >
                  <div className="w-12 h-12 bg-blue-200 rounded-full flex items-center justify-center flex-shrink-0">
                    {/* <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6 text-white">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 21v-7.5a.75.75 0 0 1 .75-.75h3a.75.75 0 0 1 .75.75V21m-4.5 0H2.36m11.14 0H18m0 0h3.64m-1.39 0V9.349M3.75 21V9.349m0 0a3.001 3.001 0 0 0 3.75-.615A2.993 2.993 0 0 0 9.75 9.75c.896 0 1.7-.393 2.25-1.016a2.993 2.993 0 0 0 2.25 1.016c.896 0 1.7-.393 2.25-1.015a3.001 3.001 0 0 0 3.75.614m-16.5 0a3.004 3.004 0 0 1-.621-4.72l1.189-1.19A1.5 1.5 0 0 1 3.129 3l1.43-.066a1.5 1.5 0 0 1 1.447 1.096l.124.496c.084.331.33.595.663.663l.333.084c.367.092.643.368.737.735l.094.377c.084.331.33.595.663.663l.333.084c.367.092.643.368.737.735l.094.377Z" />
                    </svg> */}
                    <svg viewBox="0 0 1024 1024" className="size-8" version="1.1" xmlns="http://www.w3.org/2000/svg" fill="#000000"><g id="SVGRepo_bgCarrier" strokeWidth="0"></g><g id="SVGRepo_tracerCarrier" strokeLinecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"><path d="M205.9 244.7c-35.2 0-63.9-28.7-63.9-63.9s28.6-63.9 63.9-63.9h103.3c6.1 0 11 4.9 11 11s-4.9 11-11 11H205.9c-23.1 0-41.9 18.8-41.9 41.9s18.8 41.9 41.9 41.9c6.1 0 11 4.9 11 11s-4.9 11-11 11z" fill="#86878E"></path><path d="M250 331.4h-93.2c-6.1 0-11-4.9-11-11s4.9-11 11-11H250c17.8 0 32.3-14.5 32.3-32.3 0-17.8-14.5-32.3-32.3-32.3h-45.3c-6.1 0-11-4.9-11-11s4.9-11 11-11H250c30 0 54.3 24.4 54.3 54.3S280 331.4 250 331.4z" fill="#86878E"></path><path d="M135.2 356.6c-6.1 0-11-4.9-11-11 0-19.9 16.2-36.1 36.1-36.1h31c6.1 0 11 4.9 11 11s-4.9 11-11 11h-31c-7.8 0-14.1 6.3-14.1 14.1 0 6-4.9 11-11 11z" fill="#86878E"></path><path d="M508.9 248.2v194.7h-109c-4.1 0-7.5-3.3-7.5-7.5v-70.9c0.1-64.2 52.2-116.3 116.5-116.3z" fill="#FFD400"></path><path d="M508.9 453.9h-109c-10.2 0-18.5-8.3-18.5-18.5v-70.9c0-70.3 57.2-127.4 127.4-127.4 6.1 0 11 4.9 11 11v194.7c0.1 6.2-4.8 11.1-10.9 11.1z m-105.4-22h94.4V259.8c-53 5.5-94.4 50.4-94.4 104.8v67.3z" fill="#C13105"></path><path d="M770.2 497.3c6.4 6.3 6.4 16.7 0 23C732.5 557.5 603.7 672 450.7 672S168.9 557.4 131.2 520.2c-6.4-6.3-6.4-16.7 0-23 37.7-37.2 166.5-151.7 319.5-151.7s281.7 114.6 319.5 151.8z" fill="#FFD400"></path><path d="M450.7 682.9c-67.5 0-137.7-21.3-208.6-63.5-56.6-33.6-98.6-71.7-118.6-91.4-5.2-5.1-8.1-12-8.1-19.3 0-7.3 2.9-14.2 8.1-19.3 20-19.7 62.1-57.8 118.6-91.4 70.9-42.1 141.1-63.5 208.6-63.5s137.7 21.3 208.6 63.5c56.6 33.6 98.6 71.7 118.6 91.4 5.2 5.1 8.1 12 8.1 19.3s-2.9 14.2-8.1 19.3c-20 19.7-62.1 57.8-118.6 91.4-70.9 42.2-141.1 63.5-208.6 63.5z m0-326.3c-37.8 0-171.8 10.7-311.8 148.5-1.3 1.3-1.5 2.9-1.5 3.7 0 0.8 0.2 2.3 1.5 3.7C278.9 650.3 413 661 450.7 661c37.8 0 171.8-10.7 311.8-148.5 1.3-1.3 1.5-2.9 1.5-3.7 0-0.8-0.2-2.3-1.5-3.7-140-137.8-274.1-148.5-311.8-148.5z" fill="#C13105"></path><path d="M775 508.7l153.1 88.4V420.4z" fill="#FFD400"></path><path d="M928.1 608.1c-1.9 0-3.8-0.5-5.5-1.5l-153.1-88.4c-3.4-2-5.5-5.6-5.5-9.5 0-3.9 2.1-7.6 5.5-9.5l153.1-88.4c3.4-2 7.6-2 11 0 3.4 2 5.5 5.6 5.5 9.5v176.8c0 3.9-2.1 7.6-5.5 9.5-1.7 1-3.6 1.5-5.5 1.5zM797 508.8l120.1 69.3V439.4L797 508.8zM339.9 555.8c-0.9 0-1.9-0.1-2.9-0.4-5.9-1.6-9.3-7.6-7.8-13.5 13.9-51.7-4.5-106.4-46.7-139.3-4.8-3.7-5.6-10.6-1.9-15.4s10.6-5.6 15.4-1.9c49.2 38.4 70.6 102.1 54.4 162.4-1.2 4.9-5.6 8.1-10.5 8.1zM246 538.1c-19.2 0-34.8-15.6-34.8-34.8s15.6-34.8 34.8-34.8 34.8 15.6 34.8 34.8-15.5 34.8-34.8 34.8z m0-47.7c-7.1 0-12.8 5.8-12.8 12.8 0 7.1 5.8 12.8 12.8 12.8s12.8-5.8 12.8-12.8c0.1-7.1-5.7-12.8-12.8-12.8z" fill="#C13105"></path><path d="M607.8 480.4c-3.6 0-7.2-1.8-9.3-5C575 439 529 416.3 478.2 416.3c-6.1 0-11-4.9-11-11s4.9-11 11-11c58.1 0 111.3 26.5 138.8 69.2 3.3 5.1 1.8 11.9-3.3 15.2-1.8 1.1-3.8 1.7-5.9 1.7z" fill="#FFFFFF"></path><path d="M751.2 873.4H271.5c-68.1 0-123.3-55.2-123.3-123.3 0-58.2 47.2-105.4 105.4-105.4h515.6c58.2 0 105.4 47.2 105.4 105.4-0.1 68.1-55.3 123.3-123.4 123.3z" fill="#26AEEA"></path><path d="M751.2 884.4H271.5c-74.1 0-134.3-60.3-134.3-134.3 0-64.2 52.2-116.4 116.4-116.4h515.6c64.2 0 116.4 52.2 116.4 116.4-0.1 74-60.3 134.3-134.4 134.3zM253.5 655.7c-52.1 0-94.4 42.3-94.4 94.4 0 61.9 50.4 112.3 112.3 112.3h479.7c61.9 0 112.3-50.4 112.3-112.3 0-52.1-42.3-94.4-94.4-94.4H253.5z" fill="#17217F"></path><path d="M779.5 821.9H243.1c-126.3 0-228.7-102.4-228.7-228.7h993.9c0 126.3-102.4 228.7-228.8 228.7z" fill="#26AEEA"></path><path d="M779.5 832.9H243.1c-64 0-124.2-24.9-169.5-70.2S3.4 657.2 3.4 593.2c0-6.1 4.9-11 11-11h993.9c6.1 0 11 4.9 11 11 0 64-24.9 124.2-70.2 169.5s-105.5 70.2-169.6 70.2zM25.7 604.2c2.7 54 25 104.4 63.5 143 41.1 41.1 95.8 63.8 154 63.8h536.4c58.2 0 112.8-22.6 154-63.8 38.5-38.5 60.8-88.9 63.5-143H25.7z" fill="#17217F"></path></g></svg>
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-semibold text-gray-800">{t("reviewFishItem_EditFish")}</p>
                    <p className="text-sm text-gray-600">{t("whichFish_Heading")}</p>
                  </div>
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5 text-gray-400">
                    <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
                  </svg>
                </button>

                <button
                  onClick={() => handleEditOption("../which-recipe")}
                  className="flex items-center gap-4 p-4 bg-gradient-to-r from-green-50 to-green-100 hover:from-green-100 hover:to-green-200 rounded-xl transition-all shadow-sm hover:shadow-md"
                >
                  <div className="w-12 h-12 bg-green-200 rounded-full flex items-center justify-center flex-shrink-0">
                    {/* <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6 text-white">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25" />
                    </svg> */}
                    <svg xmlns="http://www.w3.org/2000/svg" className="size-8" viewBox="0 0 512 512"><path d="M282.819 132.794h-53.638L0 89.95v344.12l229.181 42.844h53.638L512 434.07V89.95z" fill="#19ccfe"/><path d="m512 89.95-32.339-54.864-175.966 42.616C270.013 85.389 256 102.887 256 132.794h26.819L512 89.95z" fill="#f7ee00"/><path d="M256 132.794c0-29.907-14.013-47.405-47.695-55.092L32.339 35.086 0 89.95l229.181 42.844H256z" fill="#fffc99"/><path d="M0 89.95v344.12l229.181 42.844v-344.12z" fill="#9be7fe"/><path d="m142.193 165.278-.001 47.445c0 8.67-2.482 15.733-7.175 20.427-1.379 1.379-2.985 2.543-4.754 3.533v-71.404H98.915v71.402c-1.768-.99-3.373-2.154-4.751-3.531-4.695-4.695-7.176-11.758-7.175-20.427V165.28H55.642v47.441c-.001 17.163 5.655 31.891 16.358 42.594 7.276 7.276 16.417 12.213 26.917 14.631v142.421h31.347v-142.42c10.5-2.418 19.643-7.355 26.92-14.632 10.702-10.7 16.358-25.429 16.358-42.593l.001-47.445-31.35.001z" fill="#0baebc"/><path d="M397.41 160.914c-21.882 0-39.616 17.735-39.615 39.614l-.001 43.29c0 16.309 9.86 30.312 23.943 36.386v132.162h31.347V280.204c14.082-6.074 23.942-20.077 23.941-36.385l.002-43.29c-.005-21.879-17.74-39.614-39.617-39.615z" fill="#088690"/><path d="M229.177 132.796h53.635v344.116h-53.635z" fill="#71dffe"/></svg>
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-semibold text-gray-800">{t("reviewFishItem_EditRecipe")}</p>
                    <p className="text-sm text-gray-600">{t("whichRecipe_Heading").replace("{fish}", displayFishName)}</p>
                  </div>
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5 text-gray-400">
                    <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
                  </svg>
                </button>

                <button
                  onClick={() => handleEditOption("../which-amount")}
                  className="flex items-center gap-4 p-4 bg-gradient-to-r from-orange-50 to-orange-100 hover:from-orange-100 hover:to-orange-200 rounded-xl transition-all shadow-sm hover:shadow-md"
                >
                  <div className="w-12 h-12 bg-red-200 rounded-full flex items-center justify-center flex-shrink-0">
                    {/* <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6 text-white">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
                    </svg> */}
                    <svg xmlns="http://www.w3.org/2000/svg" className="size-8" viewBox="0 0 503.467 503.467"><path d="M246.733 173.933c70.827 0 128 57.173 128 128s-57.173 128-128 128-128-57.173-128-128 57.174-128 128-128z" fill="#ffd0a1" transform="translate(5 1)"/><path d="M84.6 498.2h324.267v-34.133H84.6z" fill="#ecf4f7" transform="translate(5 1)"/><path d="M246.733 284.867c9.387 0 17.067 7.68 17.067 17.067S256.12 319 246.733 319s-17.067-7.68-17.067-17.067 7.681-17.066 17.067-17.066" fill="#aab1ba" transform="translate(5 1)"/><path d="M33.4 3.267h426.667s0 17.067-34.133 42.667c0 0-34.987 29.013-119.467 39.253-17.92 2.56-37.547 3.413-59.733 3.413-23.04 0-43.52-1.707-59.733-4.267-84.48-11.947-119.467-38.4-119.467-38.4C33.4 20.333 33.4 3.267 33.4 3.267" fill="#ecf4f7" transform="translate(5 1)"/><path d="M306.467 85.187V139.8H187V84.333c16.213 2.56 36.693 4.267 59.733 4.267 22.187 0 41.814-.853 59.734-3.413" fill="#ecf4f7" transform="translate(5 1)"/><path d="M374.733 301.933c0-70.827-57.173-128-128-128s-128 57.173-128 128 57.173 128 128 128 128-57.173 128-128m51.2-128 34.133 256c0 18.773-15.36 34.133-34.133 34.133H67.533c-18.773 0-34.133-15.36-34.133-34.133l34.133-256c0-18.773 15.36-34.133 34.133-34.133H391.8c18.773 0 34.133 15.36 34.133 34.133" fill="#ecf4f7" transform="translate(5 1)"/><path d="M413.867 503.467H89.6c-2.56 0-4.267-1.707-4.267-4.267v-29.867h-12.8c-21.333 0-38.4-17.067-38.4-38.4L68.267 174.08c0-20.48 17.067-37.547 38.4-37.547H396.8c21.333 0 38.4 17.067 38.4 38.4l34.133 255.147c0 21.333-17.067 39.253-38.4 39.253h-12.8V499.2c0 2.56-1.706 4.267-4.266 4.267zm-320-8.534H409.6v-29.867c0-2.56 1.707-4.267 4.267-4.267h17.067c16.213 0 29.867-13.653 29.867-29.867l-34.133-255.147c0-17.067-13.653-30.72-29.867-30.72H106.667c-16.213 0-29.867 13.653-29.867 29.867L42.667 431.787C42.667 448 56.32 460.8 72.533 460.8h307.2c2.56 0 4.267 1.707 4.267 4.267 0 2.56-1.707 4.267-4.267 4.267H93.867v25.599zM251.733 435.2c-72.533 0-132.267-59.733-132.267-132.267S179.2 170.667 251.733 170.667 384 230.4 384 302.933 324.267 435.2 251.733 435.2zm0-256C183.467 179.2 128 234.667 128 302.933s55.467 123.733 123.733 123.733S375.467 371.2 375.467 302.933 320 179.2 251.733 179.2zm0 221.867c-2.56 0-4.267-1.707-4.267-4.267v-8.533c0-2.56 1.707-4.267 4.267-4.267s4.267 1.707 4.267 4.267v8.533c0 2.56-1.706 4.267-4.267 4.267zm0-76.8c-11.947 0-21.333-9.387-21.333-21.333 0-10.24 7.68-18.773 17.067-21.333v-72.533c0-2.56 1.707-4.267 4.267-4.267s4.267 1.707 4.267 4.267V281.6c9.387 1.707 17.067 10.24 17.067 21.333-.001 11.947-9.388 21.334-21.335 21.334zm0-34.134c-6.827 0-12.8 5.973-12.8 12.8s5.973 12.8 12.8 12.8c6.827 0 12.8-5.973 12.8-12.8s-5.973-12.8-12.8-12.8zM345.6 307.2h-8.533c-2.56 0-4.267-1.707-4.267-4.267s1.707-4.267 4.267-4.267h8.533c2.56 0 4.267 1.707 4.267 4.267s-1.707 4.267-4.267 4.267zm-179.2 0h-8.533c-2.56 0-4.267-1.707-4.267-4.267s1.707-4.267 4.267-4.267h8.533c2.56 0 4.267 1.707 4.267 4.267s-1.707 4.267-4.267 4.267zm145.067-196.267c-2.56 0-4.267-1.707-4.267-4.267v-15.36c-16.213 1.707-34.987 2.56-55.467 2.56-8.533 0-17.92 0-26.453-.853-2.56 0-4.267-2.56-4.267-4.267 0-2.56 2.56-4.267 4.267-4.267 8.533.853 17.067.853 25.6.853 124.587 0 175.787-40.96 176.64-41.813 22.187-16.213 29.013-29.013 31.573-34.987H43.52c2.56 5.973 9.387 18.773 31.573 34.987 0 0 34.987 25.6 117.76 37.547 1.707 0 3.413 1.707 3.413 4.267v21.333c0 2.56-1.707 4.267-4.267 4.267s-4.267-1.707-4.267-4.267V89.6C105.813 77.653 71.68 52.053 69.973 50.347c-34.987-26.453-35.84-44.373-35.84-46.08C34.133 1.707 35.84 0 38.4 0h426.667c2.56 0 4.267 1.707 4.267 4.267 0 1.707-.853 19.627-35.84 46.08-1.707 1.707-35.84 29.013-117.76 39.253v17.067c-.001 2.56-1.707 4.266-4.267 4.266z" fill="#51565f"/></svg>
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-semibold text-gray-800">{t("reviewFishItem_EditAmount")}</p>
                    <p className="text-sm text-gray-600">{t("whichAmount_Heading").replace("{fish}", displayFishName)}</p>
                  </div>
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5 text-gray-400">
                    <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
                  </svg>
                </button>

                <button
                  onClick={() => handleEditOption("../extra-info")}
                  className={`flex items-center gap-4 p-4 bg-gradient-to-r from-purple-50 to-purple-100 hover:from-purple-100 hover:to-purple-200 rounded-xl transition-all shadow-sm hover:shadow-md ${shouldShowExtraInfo(settings, hasSoftBone) ? "" : "hidden"}`}
                >
                  <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6 text-white">
                      <path strokeLinecap="round" strokeLinejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z" />
                    </svg>
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-semibold text-gray-800">{t("reviewFishItem_EditAdditionalInfo")}</p>
                    <p className="text-sm text-gray-600">{t("extraInfo_Heading")}</p>
                  </div>
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5 text-gray-400">
                    <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
                  </svg>
                </button>
              </div>
              
              {/* Cancel Button */}
              <button
                onClick={() => setShowEditFlyout(false)}
                className="w-full mt-4 py-3 bg-gray-100 hover:bg-gray-200 rounded-xl font-medium text-gray-700 transition-colors"
              >
                {t("reviewFishItem_CancelEdit")}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
